import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup
} from '@mui/material';
import NotificationSystem from '../components/NotificationSystem';
import { useNotification } from '../hooks/useNotification';
import { useNotifications } from '../hooks/useNotifications';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  History as HistoryIcon
} from '@mui/icons-material';

interface Scheduler {
  id: number;
  name: string;
  type: 'users' | 'photos' | 'signatures';
  frequency: 'daily' | 'weekly' | 'monthly' | 'custom';
  time: string;
  interval_minutes?: number;
  days?: number[];
  month_day?: number;
  domain?: string;
  active: boolean;
  next_execution?: string;
  last_execution?: string;
  created_at: string;
  updated_at: string;
}

const Schedulers: React.FC = () => {
  const [schedulers, setSchedulers] = React.useState<Scheduler[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  // Sistema de notificações padronizado
  const { notification, showSuccess, showError } = useNotification();
  const { addNotification, refreshNotifications } = useNotifications();

  // Estados para o modal de edição/criação
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editingScheduler, setEditingScheduler] = React.useState<Scheduler | null>(null);
  const [isNewScheduler, setIsNewScheduler] = React.useState(false);
  const [editForm, setEditForm] = React.useState({
    name: '',
    type: 'signatures' as 'users' | 'photos' | 'signatures',
    frequency: 'daily' as 'daily' | 'weekly' | 'monthly' | 'custom',
    time: '',
    interval_minutes: '',
    days: [] as number[],
    month_day: '',
    domain: '',
    active: true
  });
  const [editLoading, setEditLoading] = React.useState(false);

  // Estados para o modal de logs
  const [logsDialogOpen, setLogsDialogOpen] = React.useState(false);
  const [selectedSchedulerId, setSelectedSchedulerId] = React.useState<number | null>(null);
  const [logs, setLogs] = React.useState<any[]>([]);
  const [logsLoading, setLogsLoading] = React.useState(false);

  // Carregar schedulers do banco
  const loadSchedulers = async () => {
    try {
      setLoading(true);

      
          const token = localStorage.getItem('authToken');
    const response = await fetch('/api/schedulers', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
      


      
      if (response.ok) {
        const data = await response.json();

        setSchedulers(data);
      } else {
        const errorData = await response.json();
        console.error('Erro na resposta:', errorData);
        setError(`Erro ao carregar agendadores: ${errorData.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao carregar agendadores:', error);
      setError('Erro ao carregar agendadores');
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados ao montar o componente
  React.useEffect(() => {
    loadSchedulers();
  }, []);

  const handleToggleScheduler = async (schedulerId: number) => {
    try {

      
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/schedulers/${schedulerId}/toggle`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      

      
      if (response.ok) {
        const updatedScheduler = await response.json();

        
        setSchedulers(schedulers.map(scheduler => 
          scheduler.id === schedulerId ? updatedScheduler : scheduler
        ));
        
        showSuccess(`Agendador ${updatedScheduler.active ? 'ativado' : 'desativado'} com sucesso!`);
        
        // Adicionar notificação ao sistema
        addNotification({
          title: `Agendador ${updatedScheduler.active ? 'ativado' : 'desativado'}`,
          message: `O agendador "${updatedScheduler.name}" foi ${updatedScheduler.active ? 'ativado' : 'desativado'} com sucesso`,
          type: 'success'
        });
        
        // Atualizar notificações do backend imediatamente
        setTimeout(() => {
          refreshNotifications();
        }, 500);
      } else {
        const errorData = await response.json();
        console.error('Erro na resposta toggle:', errorData);
        showError(`Erro ao alterar status: ${errorData.error || 'Erro desconhecido'}`);
        
        // Adicionar notificação de erro
        addNotification({
          title: 'Erro ao alterar agendador',
          message: 'Falha ao alterar o status do agendador',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Erro ao alterar status do agendador:', error);
      showError('Erro ao alterar status do agendador');
    }
  };

  const handleEditScheduler = (schedulerId: number) => {
    const scheduler = schedulers.find(s => s.id === schedulerId);
    if (!scheduler) return;

    setEditingScheduler(scheduler);
    setEditForm({
      name: scheduler.name,
      type: scheduler.type,
      frequency: scheduler.frequency,
      time: scheduler.time,
      interval_minutes: scheduler.interval_minutes?.toString() || '',
      days: scheduler.days || [],
      month_day: scheduler.month_day?.toString() || '',
      domain: scheduler.domain || '',
      active: scheduler.active
    });
    setEditDialogOpen(true);
  };

  const handleEditFormChange = (field: string, value: any) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleDayToggle = (day: number) => {
    setEditForm(prev => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter(d => d !== day)
        : [...prev.days, day].sort()
    }));
  };

  const weekDays = [
    { value: 0, label: 'Domingo' },
    { value: 1, label: 'Segunda-feira' },
    { value: 2, label: 'Terça-feira' },
    { value: 3, label: 'Quarta-feira' },
    { value: 4, label: 'Quinta-feira' },
    { value: 5, label: 'Sexta-feira' },
    { value: 6, label: 'Sábado' }
  ];

  const handleSaveEdit = async () => {
    setEditLoading(true);
    try {
      const requestBody = {
        name: editForm.name,
        type: editForm.type,
        frequency: editForm.frequency,
        time: editForm.time,
        interval_minutes: editForm.interval_minutes ? parseInt(editForm.interval_minutes) : null,
        days: editForm.days,
        month_day: editForm.month_day ? parseInt(editForm.month_day) : null,
        domain: editForm.domain || null,
        active: editForm.active
      };

      let response;
      if (isNewScheduler) {

        const token = localStorage.getItem('authToken');
        response = await fetch('/api/schedulers', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });
      } else {
        if (!editingScheduler) return;

        const token = localStorage.getItem('authToken');
        response = await fetch(`/api/schedulers/${editingScheduler.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });
      }



      if (response.ok) {
        const scheduler = await response.json();

        
        if (isNewScheduler) {
          setSchedulers([scheduler, ...schedulers]);
          showSuccess('Agendador criado com sucesso!');
          
          // Adicionar notificação
          addNotification({
            title: 'Agendador criado',
            message: `O agendador "${scheduler.name}" foi criado com sucesso`,
            type: 'success'
          });
          
          // Atualizar notificações do backend imediatamente
          setTimeout(() => {
            refreshNotifications();
          }, 500);
        } else {
          setSchedulers(schedulers.map(s => 
            s.id === editingScheduler!.id ? scheduler : s
          ));
          showSuccess('Agendador atualizado com sucesso!');
          
          // Adicionar notificação
          addNotification({
            title: 'Agendador atualizado',
            message: `O agendador "${scheduler.name}" foi atualizado com sucesso`,
            type: 'success'
          });
          
          // Atualizar notificações do backend imediatamente
          setTimeout(() => {
            refreshNotifications();
          }, 500);
        }
        
        setEditDialogOpen(false);
        setEditingScheduler(null);
        setIsNewScheduler(false);
      } else {
        const errorData = await response.json();
        console.error('Erro na resposta:', errorData);
        showError(`Erro ao ${isNewScheduler ? 'criar' : 'atualizar'} agendador: ${errorData.error || 'Erro desconhecido'}`);
        
        // Adicionar notificação de erro
        addNotification({
          title: `Erro ao ${isNewScheduler ? 'criar' : 'atualizar'} agendador`,
          message: `Falha ao ${isNewScheduler ? 'criar' : 'atualizar'} o agendador`,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Erro ao salvar agendador:', error);
      showError(`Erro ao ${isNewScheduler ? 'criar' : 'atualizar'} agendador`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingScheduler(null);
    setIsNewScheduler(false);
    setEditForm({
      name: '',
      type: 'signatures',
      frequency: 'daily',
      time: '',
      interval_minutes: '',
      days: [],
      month_day: '',
      domain: '',
      active: true
    });
  };

  const handleViewLogs = async (schedulerId: number) => {
    setSelectedSchedulerId(schedulerId);
    setLogsLoading(true);
    setLogsDialogOpen(true);
    
    try {

      
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/schedulers/${schedulerId}/logs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      

      
      if (response.ok) {
        const data = await response.json();

        setLogs(data);
      } else {
        const errorData = await response.json();
        console.error('Erro na resposta logs:', errorData);
        showError(`Erro ao carregar logs: ${errorData.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao carregar logs:', error);
      showError('Erro ao carregar logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleCloseLogs = () => {
    setLogsDialogOpen(false);
    setSelectedSchedulerId(null);
    setLogs([]);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'warning': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'success': return 'Sucesso';
      case 'error': return 'Erro';
      case 'warning': return 'Aviso';
      default: return status;
    }
  };

  const handleDeleteScheduler = async (schedulerId: number) => {
    if (window.confirm('Tem certeza que deseja excluir este agendador?')) {
      try {

        
        const token = localStorage.getItem('authToken');
        const response = await fetch(`/api/schedulers/${schedulerId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        

        
        if (response.ok) {

          setSchedulers(schedulers.filter(s => s.id !== schedulerId));
          showSuccess('Agendador excluído com sucesso!');
        } else {
          const errorData = await response.json();
          console.error('Erro na resposta delete:', errorData);
          showError(`Erro ao excluir agendador: ${errorData.error || 'Erro desconhecido'}`);
        }
      } catch (error) {
        console.error('Erro ao excluir agendador:', error);
        showError('Erro ao excluir agendador');
      }
    }
  };

      const getTypeColor = (type: string) => {
      const colors = {
        users: 'primary',
        photos: 'success',
        signatures: 'warning'
      };
      return colors[type as keyof typeof colors] || 'default';
    };

    const getTypeLabel = (type: string) => {
      const labels = {
        users: 'Usuários',
        photos: 'Fotos',
        signatures: 'Assinaturas'
      };
      return labels[type as keyof typeof labels] || type;
    };

  const getFrequencyLabel = (frequency: string) => {
    const labels = {
      daily: 'Diário',
      weekly: 'Semanal',
      monthly: 'Mensal',
      custom: 'Personalizado'
    };
    return labels[frequency as keyof typeof labels] || frequency;
  };

  const formatExecutionTime = (milliseconds: number) => {
    if (!milliseconds) return 'N/A';
    
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      if (remainingMinutes > 0) {
        return `${hours}h ${remainingMinutes}min`;
      }
      return `${hours}h`;
    }
    
    if (minutes > 0) {
      const remainingSeconds = seconds % 60;
      if (remainingSeconds > 0) {
        return `${minutes}min ${remainingSeconds}s`;
      }
      return `${minutes}min`;
    }
    
    return `${seconds}s`;
  };

  const formatLogDetails = (details: any) => {
    if (!details) return null;
    
    // Se for string, tentar fazer parse
    let parsedDetails = details;
    if (typeof details === 'string') {
      try {
        parsedDetails = JSON.parse(details);
      } catch (e) {
        return details;
      }
    }
    
    // Se for objeto, formatar de forma amigável
    if (typeof parsedDetails === 'object' && parsedDetails !== null) {
      const parts: string[] = [];
      
      // Informações principais primeiro
      if (parsedDetails.total !== undefined && parsedDetails.total > 0) {
        parts.push(`${parsedDetails.total} ${parsedDetails.type === 'users' ? 'usuários' : parsedDetails.type === 'photos' ? 'fotos' : 'assinaturas'} processados`);
      }
      
      if (parsedDetails.updated !== undefined && parsedDetails.updated > 0) {
        parts.push(`${parsedDetails.updated} atualizados`);
      }
      
      if (parsedDetails.synced !== undefined && parsedDetails.synced > 0) {
        parts.push(`${parsedDetails.synced} sincronizados`);
      }
      
      if (parsedDetails.created !== undefined && parsedDetails.created > 0) {
        parts.push(`${parsedDetails.created} criados`);
      }
      
      // Informações adicionais
      if (parsedDetails.domain) {
        parts.push(`Domínio: ${parsedDetails.domain}`);
      }
      
      if (parsedDetails.error) {
        parts.push(`⚠️ ${parsedDetails.error}`);
      }
      
      return parts.length > 0 ? parts.join(' • ') : 'Sem detalhes disponíveis';
    }
    
    return typeof details === 'string' ? details : 'Detalhes não disponíveis';
  };

  const formatDateTime = (dateTime: string | null | undefined) => {
    if (!dateTime) return 'Nunca';
    return new Date(dateTime).toLocaleString('pt-BR');
  };

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh', 
      bgcolor: 'background.default', 
      py: 2
    }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Agendadores
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Gerencie tarefas agendadas do sistema
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ mb: 2 }}
          onClick={() => {
            setIsNewScheduler(true);
            setEditingScheduler(null);
            setEditForm({
              name: '',
              type: 'signatures',
              frequency: 'daily',
              time: '',
              interval_minutes: '',
              days: [],
              month_day: '',
              domain: '',
              active: true
            });
            setEditDialogOpen(true);
          }}
        >
          Novo Agendador
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : schedulers.length === 0 ? (
        <Box sx={{ mt: 4 }}>
          <Alert severity="info">Nenhum agendador encontrado.</Alert>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {schedulers.map((scheduler) => (
            <Grid item xs={12} sm={6} md={4} key={scheduler.id}>
              <Card 
                sx={{ 
                  height: 320, // Altura fixa para cards quadrados
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s ease',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                  }
                }}
              >
                {/* Header compacto */}
                <Box sx={{ 
                  p: 2, 
                  background: '#f8f9fa',
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minHeight: 60
                }}>
                  <Typography variant="h6" component="h2" sx={{ 
                    fontWeight: 600,
                    fontSize: '1rem',
                    color: '#2c3e50'
                  }}>
                    {scheduler.name}
                  </Typography>
                  <Chip
                    label={getTypeLabel(scheduler.type)}
                    color={getTypeColor(scheduler.type) as any}
                    size="small"
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      height: 24,
                      color: 'white',
                      '& .MuiChip-label': {
                        color: 'white !important'
                      },
                      '& .MuiChip-icon': {
                        color: 'white !important'
                      }
                    }}
                  />
                </Box>

                {/* Conteúdo do Card */}
                <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* Status do Agendador */}
                  <Box sx={{ mb: 1.5 }}>
                    <Chip
                      label={scheduler.active ? 'Ativo' : 'Inativo'}
                      color={scheduler.active ? 'success' : 'warning'}
                      size="small"
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        height: 24,
                        color: 'white',
                        '& .MuiChip-label': {
                          color: 'white !important'
                        },
                        '& .MuiChip-icon': {
                          color: 'white !important'
                        }
                      }}
                    />
                  </Box>

                  {/* Informações de Frequência */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                      {getFrequencyLabel(scheduler.frequency)} às {scheduler.time}
                    </Typography>
                    {scheduler.domain && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Domínio: {scheduler.domain}
                      </Typography>
                    )}
                  </Box>

                  {/* Informações de Execução */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                      Última: {formatDateTime(scheduler.last_execution)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Próxima: {formatDateTime(scheduler.next_execution)}
                    </Typography>
                  </Box>

                  {/* Botões de Ação */}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', mt: 'auto' }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditScheduler(scheduler.id)}
                      sx={{ 
                        backgroundColor: '#1976d2',
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        py: 0.5,
                        px: 1.5,
                        flex: 1,
                        '&:hover': {
                          backgroundColor: '#1565c0',
                        }
                      }}
                    >
                      Editar
                    </Button>
                    
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={scheduler.active ? <PauseIcon /> : <PlayIcon />}
                      onClick={() => handleToggleScheduler(scheduler.id)}
                      sx={{ 
                        backgroundColor: scheduler.active ? '#ff9800' : '#2e7d32',
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        py: 0.5,
                        px: 1.5,
                        flex: 1,
                        '&:hover': {
                          backgroundColor: scheduler.active ? '#f57c00' : '#1b5e20',
                        }
                      }}
                    >
                      {scheduler.active ? 'Pausar' : 'Ativar'}
                    </Button>
                    
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<HistoryIcon />}
                      onClick={() => handleViewLogs(scheduler.id)}
                      sx={{ 
                        backgroundColor: '#9c27b0',
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        py: 0.5,
                        px: 1.5,
                        flex: 1,
                        '&:hover': {
                          backgroundColor: '#7b1fa2',
                        }
                      }}
                    >
                      Logs
                    </Button>
                  </Box>
                </CardContent>

                {/* Footer com Ação Destrutiva */}
                <Box sx={{ 
                  p: 2, 
                  pt: 1,
                  borderTop: '1px solid #f0f0f0',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 50
                }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteScheduler(scheduler.id)}
                    sx={{ 
                      backgroundColor: '#d32f2f',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      py: 0.5,
                      px: 1.5,
                      '&:hover': {
                        backgroundColor: '#c62828',
                      }
                    }}
                  >
                    Excluir
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Sistema de notificações padronizado */}
      <NotificationSystem
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={notification.onClose || (() => {})}
      />

      {/* Modal de Edição/Criação */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCancelEdit}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isNewScheduler ? 'Novo Agendador' : 'Editar Agendador'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nome do Agendador"
              value={editForm.name}
              onChange={(e) => handleEditFormChange('name', e.target.value)}
              fullWidth
              required
            />
            
            <FormControl fullWidth required>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={editForm.type}
                onChange={(e) => handleEditFormChange('type', e.target.value)}
                label="Tipo"
              >
                <MenuItem value="users">Usuários</MenuItem>
                <MenuItem value="photos">Fotos</MenuItem>
                <MenuItem value="signatures">Assinaturas</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth required>
              <InputLabel>Frequência</InputLabel>
              <Select
                value={editForm.frequency}
                onChange={(e) => handleEditFormChange('frequency', e.target.value)}
                label="Frequência"
              >
                <MenuItem value="daily">Diário</MenuItem>
                <MenuItem value="weekly">Semanal</MenuItem>
                <MenuItem value="monthly">Mensal</MenuItem>
                <MenuItem value="custom">Personalizado</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Horário (HH:MM)"
              value={editForm.time}
              onChange={(e) => handleEditFormChange('time', e.target.value)}
              fullWidth
              required
              placeholder="08:00"
            />
            
            {editForm.frequency === 'custom' && (
              <TextField
                label="Intervalo em Minutos"
                value={editForm.interval_minutes}
                onChange={(e) => handleEditFormChange('interval_minutes', e.target.value)}
                fullWidth
                type="number"
                placeholder="60"
              />
            )}
            
            {editForm.frequency === 'weekly' && (
              <FormControl fullWidth>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Dias da Semana
                </Typography>
                <FormGroup row>
                  {weekDays.map((day) => (
                    <FormControlLabel
                      key={day.value}
                      control={
                        <Checkbox
                          checked={editForm.days.includes(day.value)}
                          onChange={() => handleDayToggle(day.value)}
                          size="small"
                        />
                      }
                      label={day.label}
                      sx={{ minWidth: '120px' }}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            )}
            
            {editForm.frequency === 'monthly' && (
              <TextField
                label="Dia do Mês (1-31)"
                value={editForm.month_day}
                onChange={(e) => handleEditFormChange('month_day', e.target.value)}
                fullWidth
                type="number"
                inputProps={{ min: 1, max: 31 }}
                placeholder="1"
              />
            )}
            
            <TextField
              label="Domínio (opcional)"
              value={editForm.domain}
              onChange={(e) => handleEditFormChange('domain', e.target.value)}
              fullWidth
              placeholder="exemplo.com"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.active}
                  onChange={(e) => handleEditFormChange('active', e.target.checked)}
                />
              }
              label="Ativo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} disabled={editLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained"
            disabled={editLoading || !editForm.name || !editForm.time}
          >
            {editLoading ? <CircularProgress size={20} /> : (isNewScheduler ? 'Criar' : 'Salvar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Logs */}
      <Dialog 
        open={logsDialogOpen} 
        onClose={handleCloseLogs}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Logs do Agendador
          {selectedSchedulerId && (
            <Typography variant="body2" color="text.secondary">
              ID: {selectedSchedulerId}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {logsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : logs.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Nenhum log encontrado para este agendador.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {logs.map((log, index) => (
                <Box 
                  key={log.id || index} 
                  sx={{ 
                    mb: 2, 
                    p: 2, 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 2,
                    bgcolor: log.status === 'error' ? 'error.light' : log.status === 'success' ? 'success.light' : 'background.paper',
                    borderLeft: `4px solid ${
                      log.status === 'error' ? '#f44336' : 
                      log.status === 'success' ? '#4caf50' : 
                      '#ff9800'
                    }`
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Chip
                      label={getStatusLabel(log.status)}
                      color={getStatusColor(log.status) as any}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {formatDateTime(log.created_at)}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500, color: 'text.primary' }}>
                    {log.message}
                  </Typography>
                  
                  {(log.execution_time || log.details) && (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        flexWrap: 'wrap', 
                        mt: 1.5,
                        pt: 1.5,
                        borderTop: '1px solid rgba(0,0,0,0.1)'
                      }}
                    >
                      {log.execution_time && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            ⏱️ Tempo de execução:
                          </Typography>
                          <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
                            {formatExecutionTime(log.execution_time)}
                          </Typography>
                        </Box>
                      )}
                      
                      {log.details && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, flex: 1, minWidth: '200px' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            📋
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            {formatLogDetails(log.details)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLogs}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Schedulers;
